import { useCallback, useState } from "react";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { toast } from "sonner";
import { settingsApi } from "@/lib/admin-api";
import type { SettingView } from "@/lib/settings-types";

interface ConvertToSecretDialogProps {
  /** The setting to convert; the dialog is open while this is non-null. */
  target: SettingView | null;
  onClose: () => void;
  /** Called after a successful conversion so the host grid reloads its own rows. */
  onConverted: () => void | Promise<void>;
}

/**
 * The one-way plain → secret confirmation (sc-116657), shared by the Settings page and the job details
 * page's settings card. conduit-api encrypts the value it already stores, so the operator re-types nothing;
 * there is no way back to plain, which the copy says before anything is sent.
 */
export function ConvertToSecretDialog({ target, onClose, onConverted }: ConvertToSecretDialogProps) {
  const [isConverting, setIsConverting] = useState(false);

  const confirm = useCallback(async () => {
    if (!target) return;
    setIsConverting(true);
    try {
      await settingsApi.convertToSecret(target.scope, target.key);
    } catch (err) {
      // Stays open so the operator can retry or cancel.
      toast.error(err instanceof Error ? err.message : "Failed to convert setting");
      return;
    } finally {
      setIsConverting(false);
    }
    toast.success(`Converted "${target.key}" to a secret`);
    onClose();
    await onConverted();
  }, [target, onClose, onConverted]);

  return (
    <ConfirmDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Convert to secret"
      description={`Convert ${target?.scope ?? ""} / ${target?.key ?? ""} to a secret? Its stored value is encrypted in place and masked from then on. This cannot be undone: a secret cannot be turned back into a plain setting.`}
      confirmLabel="Convert to secret"
      onConfirm={confirm}
      variant="default"
      isLoading={isConverting}
    />
  );
}
